import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { ToastProvider } from './components/Toast';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
// DashboardPage stays eager — it's the most common landing target, so lazy
// loading it would add a chunk-fetch round trip to the first visit's TTI.
import DashboardPage from './pages/DashboardPage';
import ChunkErrorBoundary from './components/ChunkErrorBoundary';
import './App.css';

// Every other route is lazy-loaded so a user landing on `/` doesn't pay the
// download cost for RayStation, Analytics, Audit Log, etc. on first paint.
// Per-route chunks fetch on demand and cache across sessions. Cuts initial
// bundle ~30-40%.
const ConstraintSetPage = lazy(() => import('./pages/ConstraintSetPage'));
const ApprovalsPage = lazy(() => import('./pages/ApprovalsPage'));
const ApprovalDetailPage = lazy(() => import('./pages/ApprovalDetailPage'));
const ConsistencyCheckPage = lazy(() => import('./pages/ConsistencyCheckPage'));
const RayStationPage = lazy(() => import('./pages/RayStationPage'));
const UserManagementPage = lazy(() => import('./pages/UserManagementPage'));
const AuditLogPage = lazy(() => import('./pages/AuditLogPage'));
const AnalyticsPage = lazy(() => import('./pages/AnalyticsPage'));
const BedCalculatorPage = lazy(() => import('./pages/BedCalculatorPage'));
const ReirradiationListPage = lazy(() => import('./pages/ReirradiationListPage'));
const ReirradiationCasePage = lazy(() => import('./pages/ReirradiationCasePage'));
const OneOffsPage = lazy(() => import('./pages/OneOffsPage'));
const OneOffSetPage = lazy(() => import('./pages/OneOffSetPage'));

function App() {
  const { user, loading, login, logout } = useAuth();

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="loading-spinner" />
        <p>Loading...</p>
      </div>
    );
  }

  if (!user) {
    return (
      <ToastProvider>
        <LoginPage onLogin={login} />
      </ToastProvider>
    );
  }

  return (
    <ToastProvider>
      <BrowserRouter>
        <Layout user={user} onLogout={logout}>
          <ChunkErrorBoundary>
            <Suspense
              fallback={
                <div className="loading-spinner" style={{ margin: '48px auto' }} />
              }
            >
              <Routes>
              <Route path="/" element={<DashboardPage user={user} />} />
              <Route path="/constraint-sets/:id" element={<ConstraintSetPage user={user} />} />
              <Route path="/approvals" element={<ApprovalsPage user={user} />} />
              <Route path="/approvals/:id" element={<ApprovalDetailPage user={user} />} />
              <Route path="/consistency" element={<ConsistencyCheckPage />} />
              <Route path="/raystation" element={<RayStationPage />} />
              <Route path="/analytics" element={<AnalyticsPage />} />
              <Route path="/audit" element={<AuditLogPage />} />
              <Route path="/tools/bed-converter" element={<BedCalculatorPage />} />
              <Route path="/one-offs" element={<OneOffsPage user={user} />} />
              <Route path="/one-off-sets/:id" element={<OneOffSetPage user={user} />} />
              {user.reirradiation_enabled && (
                <Route path="/reirradiation" element={<ReirradiationListPage user={user} />} />
              )}
              {user.reirradiation_enabled && (
                <Route path="/reirradiation/cases/:id" element={<ReirradiationCasePage user={user} />} />
              )}
              {user.is_admin && (
                <Route path="/users" element={<UserManagementPage />} />
              )}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            </Suspense>
          </ChunkErrorBoundary>
        </Layout>
      </BrowserRouter>
    </ToastProvider>
  );
}

export default App;
